import { createCourt, updateCourt } from '@/api/clubs/clubApi';
import { toast } from '@/components/ui/use-toast';
import { Court } from '@/types/club.model';
import { SportData } from '@/types/sports.model';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  CourtFormData,
  createDynamicCourtSchema,
  createSurfaceMap,
  createWallTypeMap,
} from './validations';
import { handleGlobalError } from '@/utils/error-handler';
import { useSportsStore } from '@/store/useSportsStore';

interface CourtFormProps {
  court?: Court;
  clubId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  isReadOnly?: boolean;
}

export const UpsertCourt: React.FC<CourtFormProps> = ({
  court,
  clubId,
  onSuccess,
  onCancel,
  isReadOnly = false,
}) => {
  const [sportsData, setSportsData] = useState<SportData[]>([]);
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const cachedSports = useSportsStore((s) => s.sportsData);
  const isInitialized = useSportsStore((s) => s.isInitialized);
  const fetchSports = useSportsStore((s) => s.fetchSports);

  useEffect(() => {
    // Use global cache if available, otherwise fetch once
    const init = async () => {
      try {
        if (!isInitialized) {
          await fetchSports();
        }
      } catch (error) {
        console.error('Error loading sports data:', error);
        handleGlobalError(
          error,
          'Error',
          'No se pudieron cargar los datos de deportes',
        );
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isInitialized, fetchSports]);

  useEffect(() => {
    if (cachedSports.length > 0) {
      setSportsData(cachedSports);
    }
  }, [cachedSports]);

  useEffect(() => {
    if (court?.sport?.uid && !selectedSport && sportsData.length > 0) {
      setSelectedSport(court.sport.uid);
    }
  }, [court?.sport?.uid, selectedSport, sportsData.length]);

  const getCurrentSportData = useCallback(() => {
    return sportsData.find((sport) => sport.sport.uid === selectedSport);
  }, [sportsData, selectedSport]);

  const getDefaultFormValues = useCallback((): CourtFormData => {
    const currentSportData = getCurrentSportData();

    const baseValues = {
      sport: {
        name: currentSportData?.sport.name || '',
        uid: currentSportData?.sport.uid || '',
      },
      name: '',
      surface: currentSportData?.data?.court?.surface?.[0] || '',
      price: {
        value: 0,
        currency: {
          isoCode: 'ARS',
          name: 'Pesos Argentinos',
        },
      },
      clubId: clubId,
      // Las canchas nuevas arrancan como cubiertas: es el caso mas comun.
      hasRoof: true,
    };

    if (
      currentSportData?.data?.court?.wallType &&
      currentSportData.data.court.wallType.length > 0
    ) {
      return {
        ...baseValues,
        wallType: currentSportData.data.court.wallType[0],
      } as CourtFormData;
    }

    return baseValues as CourtFormData;
  }, [getCurrentSportData, clubId]);

  const getDefaultValues = useCallback((): CourtFormData => {
    const currentSportData = getCurrentSportData();

    if (court) {
      try {
        const baseValues = {
          sport: {
            name: court.sport.name,
            uid: court.sport.uid,
          },
          name: court.name,
          surface: court.surface,
          price: court.price,
          clubId: clubId,
          hasRoof: court.hasRoof ?? false,
        };

        if (
          court.wallType &&
          currentSportData?.data?.court?.wallType &&
          currentSportData.data.court.wallType.length > 0
        ) {
          return {
            ...baseValues,
            wallType: court.wallType,
          } as CourtFormData;
        }

        return baseValues as CourtFormData;
      } catch (error) {
        console.warn(
          'Court data does not match schema, using defaults:',
          error,
        );
        return getDefaultFormValues();
      }
    }
    return getDefaultFormValues();
  }, [court, clubId, getCurrentSportData, getDefaultFormValues]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<CourtFormData>({
    resolver: zodResolver(createDynamicCourtSchema(getCurrentSportData())),
    mode: 'onChange',
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (!loading && sportsData.length > 0) {
      const defaultValues = getDefaultValues();
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, sportsData.length, court?.id]);

  useEffect(() => {
    if (selectedSport && !isReadOnly && !loading) {
      const currentSportData = sportsData.find(
        (sport) => sport.sport.uid === selectedSport
      );

      if (currentSportData) {
        if (
          currentSportData.data?.court?.surface &&
          currentSportData.data.court.surface.length > 0
        ) {
          setValue('surface', currentSportData.data.court.surface[0], {
            shouldValidate: true,
          });
        }
        if (
          currentSportData.data?.court?.wallType &&
          currentSportData.data.court.wallType.length > 0
        ) {
          setValue(
            'wallType' as keyof CourtFormData,
            currentSportData.data.court.wallType[0] as never,
            { shouldValidate: true }
          );
        }
        trigger();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSport, isReadOnly, court?.id, loading]);

  const onSubmit = async (data: CourtFormData) => {
    if (isReadOnly) return;

    const currentSportData = getCurrentSportData();
    const cleanData = { ...data };

    if (
      !currentSportData?.data?.court?.wallType ||
      currentSportData.data.court.wallType.length === 0
    ) {
      delete (cleanData as Partial<CourtFormData & { wallType?: string }>)
        .wallType;
    }

    return court?.id ? update(cleanData) : create(cleanData);
  };

  const create = async (
    data: Partial<CourtFormData & { wallType?: string }>,
  ) => {
    try {
      await createCourt(data as unknown as Court);
      toast({
        title: 'Cancha creada',
        description: 'La cancha se ha creado correctamente',
        variant: 'default',
      });
      onSuccess?.();
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudo crear la cancha',
        'Hubo un error al intentar crear esta cancha.',
      );
    }
  };

  const update = async (
    data: Partial<CourtFormData & { wallType?: string }>,
  ) => {
    try {
      await updateCourt({
        ...data,
        id: court!.id,
        clubId: clubId,
      } as Court);
      toast({
        title: 'Cancha actualizada',
        description: 'La cancha se ha actualizada correctamente',
        variant: 'default',
      });
      onSuccess?.();
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudo actualizar la cancha',
        'Hubo un error al intentar actualizar esta cancha.',
      );
    }
  };

  const handleOnValueChangeInSport = (
    field: ControllerRenderProps<CourtFormData>,
    value: string,
  ) => {
    if (isReadOnly) return;

    const sportData = sportsData.find((sport) => sport.sport.uid === value);
    if (sportData) {
      field.onChange(sportData.sport);
      setSelectedSport(value);
    }
  };

  const handleOnValueChangeInCurrency = (
    field: ControllerRenderProps<CourtFormData>,
    value: string,
  ) => {
    if (isReadOnly) return;

    const currencyMap = {
      ARS: { isoCode: 'ARS', name: 'Pesos Argentinos' },
      USD: { isoCode: 'USD', name: 'Dólares Estadounidenses' },
    };
    field.onChange(currencyMap[value as keyof typeof currencyMap]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
          <p className="mt-2 text-sm text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  const currentSportData = getCurrentSportData();
  const surfaceOptions = currentSportData?.data?.court?.surface || [];
  const wallTypeOptions = currentSportData?.data?.court?.wallType || [];
  const hasSurface = surfaceOptions.length > 0;
  const hasWallType = wallTypeOptions.length > 0;

  return (
    <div className={isReadOnly ? 'opacity-90' : ''}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div
          className={`rounded-lg border p-4 ${isReadOnly ? 'bg-gray-50' : ''}`}
        >
          <h2 className="mb-4 font-semibold">
            {court
              ? isReadOnly
                ? 'Detalles de la Cancha'
                : 'Editar Cancha'
              : 'Nueva Cancha'}
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block text-sm font-medium">Deporte</Label>
              <Controller
                control={control}
                name="sport"
                render={({ field }) => (
                  <Select
                    onValueChange={(value) =>
                      handleOnValueChangeInSport(field, value)
                    }
                    defaultValue={field.value?.uid}
                    value={field.value?.uid}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger
                      className={`w-full ${isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''}`}
                    >
                      <SelectValue placeholder="Seleccionar deporte" />
                    </SelectTrigger>
                    <SelectContent>
                      {sportsData.map((sport) => (
                        <SelectItem
                          key={sport.sport.uid}
                          value={sport.sport.uid}
                        >
                          {sport.sport.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.sport?.name && (
                <p className="text-sm text-red-500">
                  {errors.sport.name.message}
                </p>
              )}
            </div>

            <div>
              <Label className="mb-2 block text-sm font-medium">Nombre</Label>
              <Input
                {...register('name')}
                className={`w-full rounded border p-2 ${
                  isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="Nombre de la cancha"
                readOnly={isReadOnly}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {hasSurface && (
              <div>
                <Label className="mb-2 block text-sm font-medium">
                  Superficie
                </Label>
                <Controller
                  control={control}
                  name="surface"
                  render={({ field }) => (
                    <Select
                      onValueChange={isReadOnly ? undefined : field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger
                        className={`w-full ${isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''}`}
                      >
                        <SelectValue placeholder="Seleccionar superficie" />
                      </SelectTrigger>
                      <SelectContent>
                        {surfaceOptions.map((surface) => (
                          <SelectItem key={surface} value={surface}>
                            {createSurfaceMap(surfaceOptions)[surface] ||
                              surface}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.surface && (
                  <p className="text-sm text-red-500">
                    {errors.surface.message}
                  </p>
                )}
              </div>
            )}

            {hasWallType && (
              <div>
                <Label className="mb-2 block text-sm font-medium">
                  Tipo de Pared
                </Label>
                <Controller
                  control={control}
                  name={'wallType' as keyof CourtFormData}
                  render={({ field }) => (
                    <Select
                      onValueChange={isReadOnly ? undefined : field.onChange}
                      defaultValue={field.value as string | undefined}
                      value={field.value as string | undefined}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger
                        className={`w-full ${isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''}`}
                      >
                        <SelectValue placeholder="Seleccionar tipo de pared" />
                      </SelectTrigger>
                      <SelectContent>
                        {wallTypeOptions.map((wallType) => (
                          <SelectItem key={wallType} value={wallType}>
                            {createWallTypeMap(wallTypeOptions)[wallType] ||
                              wallType}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {(errors as Record<string, { message?: string }>).wallType && (
                  <p className="text-sm text-red-500">
                    {
                      (errors as Record<string, { message?: string }>).wallType
                        .message
                    }
                  </p>
                )}
              </div>
            )}

            <div>
              <Label className="mb-2 block text-sm font-medium">Precio</Label>
              <Input
                type="number"
                {...register('price.value', {
                  valueAsNumber: true,
                })}
                className={`w-full rounded border p-2 ${
                  isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''
                }`}
                placeholder="0"
                readOnly={isReadOnly}
              />
              {errors.price?.value && (
                <p className="text-sm text-red-500">
                  {errors.price.value.message}
                </p>
              )}
            </div>

            <div>
              <Label className="mb-2 block text-sm font-medium">Moneda</Label>
              <Controller
                control={control}
                name="price.currency"
                render={({ field }) => (
                  <Select
                    onValueChange={(value) =>
                      handleOnValueChangeInCurrency(field, value)
                    }
                    defaultValue={field.value?.isoCode}
                    value={field.value?.isoCode}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger
                      className={`w-full ${isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''}`}
                    >
                      <SelectValue placeholder="Seleccionar moneda" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ARS">ARS</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.price?.currency?.isoCode && (
                <p className="text-sm text-red-500">
                  {errors.price.currency.isoCode.message}
                </p>
              )}
            </div>

            <div className="col-span-2 flex items-center gap-3">
              <Controller
                control={control}
                name="hasRoof"
                render={({ field }) => (
                  <Checkbox
                    id="court-has-roof"
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    disabled={isReadOnly}
                  />
                )}
              />
              <Label
                htmlFor="court-has-roof"
                className={`text-sm font-medium ${
                  isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                Cancha cubierta
              </Label>
            </div>
          </div>

          {!isReadOnly && (
            <div className="mt-6 flex gap-4">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              )}
              <Button
                type="submit"
                variant="default"
                className="flex-1"
                disabled={!isValid || isSubmitting}
              >
                {court ? 'Actualizar' : 'Crear'} Cancha
              </Button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};
